import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Upload, Sparkles, ArrowRight, RefreshCw, X, Users, AlertCircle, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";

const BRANDS = ["ASOS", "Mango", "Meski", "New Look", "Next", "Zara", "River Island", "Topshop"];

const empty = {
    title: "",
    brand: "",
    price: "",
    currency: "GBP",
    description: "",
    image_url: "",
    image_storage_path: "",
    source_url: "",
    category: "",
    tags: [],
    visible_to: [],
};

export default function AddProduct() {
    const nav = useNavigate();
    const [url, setUrl] = useState("");
    const [scraping, setScraping] = useState(false);
    const [scrapeError, setScrapeError] = useState(null); // { detail, isListing }
    const [form, setForm] = useState(empty);
    const [savedFileUrl, setSavedFileUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [clients, setClients] = useState([]);
    const fileRef = useRef(null);

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }, []);

    const scrape = async () => {
        if (!url.startsWith("http")) return toast.error("Paste a valid product URL");
        setScraping(true);
        setScrapeError(null);
        setForm(empty);
        setSavedFileUrl("");

        // Best-effort title guess from URL slug so the form is pre-filled even if scrape fails.
        const guessTitleFromUrl = (u) => {
            try {
                const path = new URL(u).pathname;
                // Take the meaningful segments (dashes → spaces, drop numeric-only ones)
                const parts = path.split("/").filter((s) => s && !/^[a-zA-Z]?\d+$/.test(s) && !/^prd|style|product$/.test(s));
                const slug = parts[parts.length - 1] || "";
                return decodeURIComponent(slug).replace(/\.html?$/i, "").replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).slice(0, 120);
            } catch { return ""; }
        };
        const guessBrandFromUrl = (u) => {
            try {
                const host = new URL(u).hostname.toLowerCase();
                const map = {
                    "asos.com": "ASOS", "zara.com": "Zara", "mango.com": "Mango",
                    "next.co.uk": "Next", "newlook.com": "New Look", "topshop.com": "Topshop",
                    "riverisland.com": "River Island", "meski.com": "Meski",
                };
                return Object.entries(map).find(([h]) => host.includes(h))?.[1] || "";
            } catch { return ""; }
        };

        try {
            const r = await api.post("/products/scrape", { url });
            const d = r.data;
            setForm({
                ...empty,
                title: d.title || "",
                brand: d.brand || "",
                price: d.price ? String(d.price) : "",
                currency: d.currency || "GBP",
                description: d.description || "",
                image_url: d.image_url || "",
                source_url: d.source_url || url,
            });
            toast.success("Auto-fetched from brand page. Review & save.");
        } catch (e) {
            const detail = e?.response?.data?.detail || "Scrape failed — you can still fill manually";
            const isListing = /listing page|category/i.test(String(detail));
            setScrapeError({ detail, isListing });
            if (!isListing) toast.error(String(detail).slice(0, 140));
            // Even on failure, pre-fill what we can so the user has less typing
            setForm({
                ...empty,
                source_url: url,
                title: !isListing ? guessTitleFromUrl(url) : "",
                brand: !isListing ? guessBrandFromUrl(url) : "",
            });
        } finally {
            setScraping(false);
        }
    };

    const uploadFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((prev) => ({ ...prev, image_storage_path: r.data.storage_path, image_url: "" }));
            setSavedFileUrl(fileUrl(r.data.storage_path));
            toast.success("Image uploaded");
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const toggleTag = (t) => {
        const has = form.tags.includes(t);
        setForm({ ...form, tags: has ? form.tags.filter((x) => x !== t) : [...form.tags, t] });
    };

    const save = async () => {
        if (!form.title || !form.brand || !form.price) return toast.error("Title, brand and price are required");
        try {
            await api.post("/products", { ...form, price: parseFloat(form.price) });
            toast.success("Product added to catalog");
            nav("/admin/catalog");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        }
    };

    const previewSrc = savedFileUrl || form.image_url;
    const hasData = form.title || form.image_url || form.image_storage_path;

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-6xl">
            <div className="mb-6 lg:mb-10">
                <div className="overline text-neutral-500">Import</div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none mt-2">Add Product</h1>
                <p className="font-body text-sm text-neutral-500 mt-3 max-w-xl leading-relaxed">
                    Paste any product URL from a partner brand. We&apos;ll auto-fetch the title, price, description and image — you review and publish.
                </p>
            </div>

            {/* Scrape bar */}
            <div className="border border-black bg-neutral-50 p-4 sm:p-6 mb-6 sm:mb-8">
                <div className="overline text-neutral-600 mb-3 flex items-center gap-2"><Sparkles className="h-3 w-3" strokeWidth={1.5}/> Auto-Fetch from URL</div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                        data-testid="scrape-url-input"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="Paste a SINGLE PRODUCT URL (not a category page)…"
                        className="rounded-none h-12 border-black bg-white flex-1"
                        onKeyDown={(e) => e.key === "Enter" && scrape()}
                    />
                    <Button
                        onClick={scrape}
                        disabled={scraping}
                        data-testid="scrape-btn"
                        className="rounded-none h-12 bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs font-semibold px-8 group"
                    >
                        {scraping ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5}/>Fetching</>) : (<>Fetch <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5}/></>)}
                    </Button>
                </div>

                {/* URL type guide */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-body" data-testid="url-guide">
                    <div className="border border-green-500 bg-green-50 p-3">
                        <div className="uppercase tracking-widest text-[10px] font-semibold text-green-800 mb-1">✓ Right — Single Product</div>
                        <div className="font-mono text-green-900 break-all">
                            asos.com/prd/<b>210814590</b><br/>
                            zara.com/uk/en/textured-t-shirt-<b>p05643803</b>.html<br/>
                            next.co.uk/style/some-slug/<b>A12345</b>
                        </div>
                    </div>
                    <div className="border border-red-500 bg-red-50 p-3">
                        <div className="uppercase tracking-widest text-[10px] font-semibold text-red-800 mb-1">✗ Wrong — Category / Listing</div>
                        <div className="font-mono text-red-900 break-all">
                            asos.com/women/new-in/cat/<br/>
                            next.co.uk/shop/womens/f/feat-newin<br/>
                            newlook.com/uk/womens/clothing/new
                        </div>
                    </div>
                </div>

                {/* Listing error callout */}
                {scrapeError?.isListing && (
                    <div className="mt-4 border-2 border-orange-500 bg-orange-50 p-4 flex items-start gap-3" data-testid="listing-error-callout">
                        <AlertCircle className="h-5 w-5 text-orange-700 flex-shrink-0 mt-0.5" strokeWidth={1.5}/>
                        <div className="flex-1">
                            <div className="uppercase tracking-widest text-[10px] font-bold text-orange-800">You pasted a listing page</div>
                            <div className="text-xs font-body text-orange-900 mt-1 leading-relaxed">
                                That URL shows many products. Add Product only accepts a SINGLE product page.
                                Click any product on that listing → copy the new URL (with a numeric SKU) → paste it here.
                                Want all products from that page? Use <b>Bulk Paste</b>.
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Link
                                    to="/admin/bulk"
                                    className="inline-flex items-center gap-1 bg-orange-700 text-white uppercase tracking-widest text-[10px] font-semibold px-3 h-8 hover:bg-orange-800"
                                    data-testid="go-to-bulk-paste"
                                >
                                    <ExternalLink className="h-3 w-3" strokeWidth={1.5}/> Go To Bulk Paste
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => { setUrl(""); setScrapeError(null); }}
                                    className="inline-flex items-center gap-1 border border-orange-700 text-orange-700 uppercase tracking-widest text-[10px] font-semibold px-3 h-8 hover:bg-orange-100"
                                    data-testid="clear-url-btn"
                                >
                                    Clear & Try Another URL
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Generic error callout */}
                {scrapeError && !scrapeError.isListing && (
                    <div className="mt-4 border-2 border-red-500 bg-red-50 p-4 flex items-start gap-3" data-testid="scrape-error-callout">
                        <AlertCircle className="h-5 w-5 text-red-700 flex-shrink-0 mt-0.5" strokeWidth={1.5}/>
                        <div className="text-xs font-body text-red-900 leading-relaxed">
                            <b>{scrapeError.detail}</b>
                            <div className="mt-1">You can still fill the fields below manually and save the product.</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Preview column */}
                <div className="lg:col-span-2">
                    <div className="overline text-neutral-500 mb-2">Preview</div>
                    <div className="aspect-[3/4] border border-border bg-neutral-50 relative overflow-hidden">
                        {scraping ? (
                            <Skeleton className="w-full h-full rounded-none"/>
                        ) : previewSrc ? (
                            <img src={previewSrc} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs uppercase tracking-widest">No image</div>
                        )}
                    </div>
                    <div className="mt-4 flex gap-2">
                        <Button
                            variant="outline"
                            className="rounded-none border-black flex-1"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid="upload-image-btn"
                        >
                            <Upload className="h-4 w-4 mr-2" strokeWidth={1.5}/> {uploading ? "Uploading…" : "Upload Image"}
                        </Button>
                        {form.image_storage_path && (
                            <Button variant="ghost" size="icon" className="rounded-none" onClick={() => { setForm({...form, image_storage_path: ""}); setSavedFileUrl(""); }}>
                                <X className="h-4 w-4" strokeWidth={1.5}/>
                            </Button>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadFile} data-testid="file-input"/>
                    {form.source_url && (
                        <div className="mt-3 text-xs font-body text-neutral-500 break-all">
                            <div className="overline mb-1">Source</div>
                            <a href={form.source_url} target="_blank" rel="noreferrer" className="hover:text-black underline underline-offset-2">{form.source_url}</a>
                        </div>
                    )}
                </div>

                {/* Form column */}
                <div className="lg:col-span-3 space-y-4">
                    {scraping ? (
                        <div className="space-y-4">
                            <Skeleton className="h-5 w-24 rounded-none"/>
                            <Skeleton className="h-12 w-full rounded-none"/>
                            <div className="grid grid-cols-2 gap-4">
                                <Skeleton className="h-12 rounded-none"/>
                                <Skeleton className="h-12 rounded-none"/>
                            </div>
                            <Skeleton className="h-24 w-full rounded-none"/>
                        </div>
                    ) : (
                        <>
                            <div>
                                <Label className="overline text-neutral-600">Title</Label>
                                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-black mt-2 h-11" data-testid="form-title-input"/>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="overline text-neutral-600">Brand</Label>
                                    <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                                        <SelectTrigger className="rounded-none border-black mt-2 h-11" data-testid="form-brand-select"><SelectValue placeholder="Select brand"/></SelectTrigger>
                                        <SelectContent>
                                            {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="overline text-neutral-600">Category</Label>
                                    <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
                                        <SelectTrigger className="rounded-none border-black mt-2 h-11" data-testid="form-category-select">
                                            <SelectValue placeholder="Select category"/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="overline text-neutral-600">Price / pc</Label>
                                    <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none border-black mt-2 h-11" data-testid="form-price-input"/>
                                </div>
                                <div>
                                    <Label className="overline text-neutral-600">Currency</Label>
                                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                        <SelectTrigger className="rounded-none border-black mt-2 h-11"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="GBP">GBP (£)</SelectItem>
                                            <SelectItem value="USD">USD ($)</SelectItem>
                                            <SelectItem value="EUR">EUR (€)</SelectItem>
                                            <SelectItem value="INR">INR (₹)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="overline text-neutral-600">Description</Label>
                                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-black mt-2 min-h-[110px]" rows={4}/>
                            </div>
                            <div>
                                <Label className="overline text-neutral-600 block mb-2">Tags</Label>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => toggleTag("new_arrival")} className={`px-3 py-2 border uppercase tracking-widest text-[10px] ${form.tags.includes("new_arrival") ? "bg-black text-white border-black" : "border-black text-black hover:bg-neutral-100"}`} data-testid="tag-new-arrival">New Arrival</button>
                                    <button type="button" onClick={() => toggleTag("best_seller")} className={`px-3 py-2 border uppercase tracking-widest text-[10px] ${form.tags.includes("best_seller") ? "bg-yellow-400 border-black" : "border-black text-black hover:bg-neutral-100"}`} data-testid="tag-best-seller">Best Seller</button>
                                </div>
                            </div>
                            <div>
                                <Label className="overline text-neutral-600 block mb-2 flex items-center gap-2">
                                    <Users className="h-3 w-3" strokeWidth={1.5}/> Push to Clients
                                </Label>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, visible_to: [] })}
                                        className={`px-3 py-2 border uppercase tracking-widest text-[10px] ${form.visible_to.length === 0 ? "bg-black text-white border-black" : "border-black text-black hover:bg-neutral-100"}`}
                                        data-testid="visible-all"
                                    >
                                        All Clients
                                    </button>
                                    {clients.map((c) => {
                                        const on = form.visible_to.includes(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => {
                                                    const has = form.visible_to.includes(c.id);
                                                    const next = has ? form.visible_to.filter((x) => x !== c.id) : [...form.visible_to, c.id];
                                                    setForm({ ...form, visible_to: next });
                                                }}
                                                data-testid={`visible-client-${c.login_id}`}
                                                style={on ? { background: c.brand_color || "#0A0A0A", borderColor: c.brand_color || "#0A0A0A", color: "#fff" } : {}}
                                                className={`px-3 py-2 border uppercase tracking-widest text-[10px] ${on ? "" : "border-black text-black hover:bg-neutral-100"}`}
                                            >
                                                {c.brand_display_name || c.company || c.name} · ID {c.login_id}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="text-[11px] text-neutral-500 mt-2 font-body">
                                    Click a client to make this product exclusive to them. Leave &quot;All Clients&quot; selected to push to everyone.
                                </div>
                            </div>
                            <div className="flex gap-3 pt-4 border-t border-border mt-6">
                                <Button variant="outline" className="rounded-none border-black" onClick={() => { setForm(empty); setUrl(""); setSavedFileUrl(""); }}>Reset</Button>
                                <Button
                                    onClick={save}
                                    disabled={!hasData}
                                    className="rounded-none bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs font-semibold flex-1 h-11"
                                    data-testid="save-product-btn"
                                >
                                    Publish to Catalog <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5}/>
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
